import React, { useState, useEffect } from 'react';
import './UserDashboard.css';
import UserSettings from './UserSettings';

function UserDashboard() {
  const [activeProject, setActiveProject] = useState(null);
  const [userName, setUserName] = useState('Guest');
  const [showSettings, setShowSettings] = useState(false);
  const [currentUsername, setCurrentUsername] = useState(null);

  const projects = [
    {
      id: 'al-chat',
      name: 'AL-Chat',
      description: 'AI-powered chat assistant',
      icon: '💬'
    },
    {
      id: 'resume-assistant',
      name: 'Resume Assistant',
      description: 'AI-powered resume builder and optimizer',
      icon: '📄'
    }
  ];

  const handleProjectClick = (project) => {
    setActiveProject(project);
    // TODO: Implement project-specific functionality
    console.log('Selected project:', project);
  };

  // Fetch user information on mount
  useEffect(() => {
    const username = localStorage.getItem('chat_username');
    console.log('UserDashboard: Checking username from localStorage:', username);
    
    if (username) {
      setCurrentUsername(username); // Store username for settings
      // Fetch user details to get firstName/lastName
      console.log('UserDashboard: Fetching user info for username:', username);
      fetch(`/api/users/username/${encodeURIComponent(username)}`)
        .then(response => {
          console.log('UserDashboard: API response status:', response.status);
          if (response.ok) {
            return response.json();
          }
          throw new Error(`User not found: ${response.status}`);
        })
        .then(data => {
          console.log('UserDashboard: API response data:', data);
          if (data.user) {
            const { firstName, lastName, username: userUsername } = data.user;
            console.log('UserDashboard: User data:', { firstName, lastName, username: userUsername });
            // Use firstName if available, otherwise use username, fallback to "Guest"
            if (firstName) {
              setUserName(firstName); // Only show first name
            } else {
              setUserName(userUsername || username || 'Guest');
            }
          } else {
            // No user data in response, use username from localStorage
            console.log('UserDashboard: No user data in response, using username from localStorage');
            setUserName(username);
          }
        })
        .catch(error => {
          console.error('UserDashboard: Error fetching user info:', error);
          // Fallback to username from localStorage or "Guest"
          setUserName(username || 'Guest');
        });
    } else {
      // Guest access - keep "Guest" as the name
      console.log('UserDashboard: No username in localStorage, showing Guest');
      setUserName('Guest');
    }
  }, []);

  const handleLogout = () => {
    sessionStorage.removeItem('guest_access');
    sessionStorage.removeItem('access_code');
    localStorage.removeItem('chat_auth_token');
    localStorage.removeItem('chat_username');
    window.location.href = '/';
  };

  return (
    <div className="user-dashboard">
      <aside className="dashboard-sidebar">
        <div className="sidebar-header">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <h2 className="welcome-message">Welcome {userName}!</h2>
            {currentUsername && (
              <button
                onClick={() => setShowSettings(true)}
                className="settings-button"
                title="Settings"
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  transition: 'background 0.2s',
                  color: '#666'
                }}
                onMouseEnter={(e) => e.target.style.background = '#f0f0f0'}
                onMouseLeave={(e) => e.target.style.background = 'none'}
              >
                ⚙️
              </button>
            )}
          </div>
        </div>
        
        <nav className="sidebar-nav">
          <div className="nav-section">
            <h3 className="nav-section-title">Project Tools</h3>
            <ul className="project-list">
              {projects.map((project) => (
                <li key={project.id}>
                  <button
                    className={`project-item ${activeProject?.id === project.id ? 'active' : ''}`}
                    onClick={() => handleProjectClick(project)}
                  >
                    <span className="project-icon">{project.icon}</span>
                    <span className="project-name">{project.name}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </nav>
        
        <div className="sidebar-footer">
          <button className="logout-button" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </aside>

      <main className="dashboard-main">
        <div className="dashboard-content">
          {activeProject ? (
            <div className="project-view">
              <h1>{activeProject.icon} {activeProject.name}</h1>
              <p className="project-description">{activeProject.description}</p>
              <div className="project-placeholder">
                <p>Project functionality coming soon...</p>
              </div>
            </div>
          ) : (
            <div className="dashboard-welcome">
              <h1>Welcome to Papita.org</h1>
              <p>Select a project tool from the sidebar to get started.</p>
              <div className="project-cards">
                {projects.map((project) => (
                  <div
                    key={project.id}
                    className="project-card"
                    onClick={() => handleProjectClick(project)}
                  >
                    <div className="project-card-icon">{project.icon}</div>
                    <h3>{project.name}</h3>
                    <p>{project.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>

      {showSettings && currentUsername && (
        <UserSettings
          username={currentUsername}
          onClose={() => setShowSettings(false)}
          onUpdate={(newUsername) => {
            setCurrentUsername(newUsername);
            // Reload user info to get updated name
            const fetchUserInfo = async () => {
              try {
                const response = await fetch(`/api/users/username/${encodeURIComponent(newUsername)}`);
                if (response.ok) {
                  const data = await response.json();
                  if (data.user) {
                    const { firstName } = data.user;
                    if (firstName) {
                      setUserName(firstName); // Only show first name
                    } else {
                      setUserName(newUsername);
                    }
                  }
                }
              } catch (error) {
                console.error('Error fetching updated user info:', error);
                setUserName(newUsername);
              }
            };
            fetchUserInfo();
          }}
        />
      )}
    </div>
  );
}

export default UserDashboard;
